import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, functions } from '../firebase';
import Logo from '../components/Logo';
import './SubscriptionPlans.css';

const SubscriptionPlans = () => {
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPlan, setProcessingPlan] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchPlansAndPrices();
    if (user) {
      fetchUserSubscription();
    }
  }, [user]);

  const fetchUserSubscription = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.subscription_status && userData.subscription_type) {
          setUserSubscription({
            status: userData.subscription_status,
            type: userData.subscription_type,
            planId: userData.subscription_plan_id,
            priceId: userData.subscription_price_id,
            quantity: userData.subscription_quantity,
            currentPeriodEnd: userData.subscription_current_period_end,
            subscriptionId: userData.subscription_id
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    }
  };

  const fetchPlansAndPrices = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch active plans
      const plansQuery = query(
        collection(db, 'plans'),
        where('active', '==', true)
      );
      const plansSnapshot = await getDocs(plansQuery);
      
      const plansWithPrices = await Promise.all(
        plansSnapshot.docs.map(async (planDoc) => {
          const planData = { id: planDoc.id, ...planDoc.data() };
          
          // Fetch active prices for this plan
          const pricesQuery = query(
            collection(db, 'plans', planDoc.id, 'plan_prices'),
            where('active', '==', true)
          );
          const pricesSnapshot = await getDocs(pricesQuery);
          
          const prices = pricesSnapshot.docs.map(priceDoc => ({
            id: priceDoc.id,
            ...priceDoc.data()
          }));

          return {
            ...planData,
            prices: prices
          };
        })
      );

      setPlans(plansWithPrices);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Falha ao carregar planos de assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId) => {
    if (!user) return;

    // Check if user already has an active subscription
    if (userSubscription && ['active', 'trialing'].includes(userSubscription.status)) {
      if (userSubscription.type === 'manual') {
        setError('Você não pode criar uma assinatura Stripe enquanto tem uma assinatura manual ativa.');
        return;
      }
      if (userSubscription.type === 'stripe') {
        setError('Você já tem uma assinatura ativa. Por favor, cancele-a primeiro para assinar um plano diferente.');
        return;
      }
    }

    try {
      setProcessingPlan(priceId);
      setError('');

      const createStripeSubscriptionSession = httpsCallable(functions, 'createStripeSubscriptionSession');
      const result = await createStripeSubscriptionSession({ priceId });
      
      if (result.data && result.data.url) {
        // Redirect to Stripe checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('Nenhuma URL de checkout recebida');
      }
    } catch (error) {
      console.error('Error creating subscription session:', error);
      setError('Falha ao criar sessão de assinatura. Tente novamente.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const formatCurrency = (amount, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatBillingCycle = (cycle) => {
    const cycles = {
      'monthly': 'Mensal',
      'yearly': 'Anual',
      'weekly': 'Semanal',
      'daily': 'Diário'
    };
    return cycles[cycle] || cycle;
  };

  if (loading) {
    return (
      <div className="subscription-plans">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Carregando planos de assinatura...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plans">
      <div className="container">
        <header className="page-header">
          <h1>Escolha Seu Plano de Assinatura</h1>
          <p>Selecione um plano que atende às suas necessidades.</p>
          <p>Você será cobrado pelo mínimo de 1 dispositivo.</p>
        </header>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {userSubscription && ['active', 'trialing'].includes(userSubscription.status) && (
          <div className="current-subscription-notice">
            <h3>Assinatura Atual</h3>
            <p>
              Você tem uma assinatura {userSubscription.status} {userSubscription.type} 
              {userSubscription.currentPeriodEnd && (
                <> válida até {userSubscription.currentPeriodEnd.toDate().toLocaleDateString('pt-BR')}</>
              )}
            </p>
          </div>
        )}

        <div className="plans-grid">
          {plans.map(plan => (
            <div key={plan.id} className="plan-card">
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="plan-prices">
                {plan.prices.map(price => (
                  <div key={price.id} className="price-option">
                    <div className="price-info">
                      <span className="price-amount">
                        {formatCurrency(price.price_value)}
                      </span>
                      <span className="price-cycle">
                        / {formatBillingCycle(price.billing_cycle)}
                      </span>
                      <span className="price-per-device">por dispositivo</span>
                    </div>
                    
                    <button
                      className={`subscribe-btn ${processingPlan === price.id ? 'loading' : ''}`}
                      onClick={() => handleSubscribe(price.id)}
                      disabled={processingPlan === price.id || !user}
                    >
                      {processingPlan === price.id ? (
                        <>
                          <div className="spinner small"></div>
                          Processando...
                        </>
                      ) : (
                        'Assinar'
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {plan.prices.length === 0 && (
                <div className="no-prices">
                  <p>Nenhuma opção de preço ativa disponível para este plano.</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {plans.length === 0 && !loading && (
          <div className="no-plans">
            <h3>Nenhum plano disponível</h3>
            <p>Atualmente não há planos de assinatura ativos disponíveis.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlans;